import React from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Loader2, GripVertical, X } from "lucide-react";
import { logger } from "@/lib/logger";

export default function BeatBoard() {
  const { projectId } = useParams();
  const [columns, setColumns] = React.useState([]);
  const [beats, setBeats] = React.useState([]);
  const [templates, setTemplates] = React.useState([]);
  const [activeTemplate, setActiveTemplate] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [showTpl, setShowTpl] = React.useState(false);
  const [applying, setApplying] = React.useState(false);
  const [drag, setDrag] = React.useState(null); // {id, fromCol}
  const [openBeat, setOpenBeat] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      const [b, t] = await Promise.all([
        api.get(`/projects/${projectId}/beats`),
        api.get(`/beats/templates`),
      ]);
      setColumns(b.data.columns);
      setBeats(b.data.beats);
      setActiveTemplate(b.data.template);
      setTemplates(t.data);
    } catch (err) {
      logger.warn("beat board: load failed", err);
      toast.error("Could not load beats");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { load(); }, [load]);

  const applyTemplate = async (id, replace = true) => {
    setApplying(true);
    try {
      await api.post(`/projects/${projectId}/beats/apply-template`, { template_id: id, replace });
      setShowTpl(false);
      toast.success("Template applied");
      load();
    } catch (err) {
      toast.error("Could not apply template");
    } finally {
      setApplying(false);
    }
  };

  const addBeat = async (col) => {
    const { data } = await api.post(`/projects/${projectId}/beats`, {
      project_id: projectId, column: col, title: "New beat", description: "",
    });
    setBeats((bs) => [...bs, data]);
  };

  const removeBeat = async (id) => {
    await api.delete(`/projects/${projectId}/beats/${id}`);
    setBeats((bs) => bs.filter((b) => b.id !== id));
    setOpenBeat(null);
  };

  const saveBeat = async (b) => {
    await api.patch(`/projects/${projectId}/beats/${b.id}`, {
      title: b.title, description: b.description, column: b.column,
    });
    setBeats((bs) => bs.map((x) => (x.id === b.id ? { ...x, ...b } : x)));
    setOpenBeat(null);
    toast.success("Saved");
  };

  const onDropTo = async (targetCol) => {
    if (!drag) return;
    const src = beats.find((b) => b.id === drag.id);
    if (!src) return;
    if (src.column === targetCol) { setDrag(null); return; }
    // find last order in target column
    const targetOrder = Math.max(0, ...beats.filter((b) => b.column === targetCol).map((b) => b.order || 0)) + 1;
    const updated = beats.map((b) => b.id === drag.id ? { ...b, column: targetCol, order: targetOrder } : b);
    setBeats(updated);
    setDrag(null);
    try {
      await api.post(`/projects/${projectId}/beats/reorder`, {
        beats: [{ id: src.id, column: targetCol, order: targetOrder }],
      });
    } catch (err) {
      toast.error("Move failed");
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="uppercase text-xs tracking-[0.3em] text-[#D4AF37] mb-2">Beat Board</div>
            <h1 className="font-editorial text-4xl">The structure</h1>
            {activeTemplate && <div className="text-xs text-[#71717A] mt-1 uppercase tracking-widest">Template · {activeTemplate.replace(/_/g, " ")}</div>}
          </div>
          <button onClick={() => setShowTpl(true)}
            className="bg-[#D4AF37] text-[#0A0A0A] px-4 py-2 text-sm font-medium hover:bg-[#F59E0B] inline-flex items-center gap-2 transition-colors"
            data-testid="beat-templates-btn">
            <Sparkles size={14} /> Templates
          </button>
        </div>

        {loading ? <div className="text-[#A1A1AA]">Loading…</div> : (
          <div className="grid gap-4 overflow-x-auto pb-4"
            style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(240px, 1fr))` }}>
            {columns.map((col) => (
              <BoardColumn key={col}
                title={col}
                beats={beats.filter((b) => b.column === col).sort((a, b) => a.order - b.order)}
                onAdd={() => addBeat(col)}
                onEdit={(b) => setOpenBeat(b)}
                onDragStart={(id) => setDrag({ id, fromCol: col })}
                onDrop={() => onDropTo(col)}
              />
            ))}
          </div>
        )}
      </div>

      {showTpl && (
        <div className="fixed inset-0 bg-[#0A0A0A]/85 z-50 flex items-center justify-center p-6" onClick={() => setShowTpl(false)}>
          <div className="w-full max-w-2xl bg-[#141414] border border-[#27272A] p-8" onClick={(e) => e.stopPropagation()} data-testid="beat-templates-modal">
            <div className="uppercase text-xs tracking-widest text-[#D4AF37] mb-3">Templates</div>
            <h2 className="font-editorial text-3xl mb-6">Choose a structural spine</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {templates.map((t) => (
                <button key={t.id} onClick={() => applyTemplate(t.id)} disabled={applying}
                  className="text-left border border-[#27272A] hover:border-[#D4AF37] p-4 transition-colors group"
                  data-testid={`beat-template-${t.id}`}>
                  <div className="font-editorial text-xl mb-1">{t.name}</div>
                  <div className="text-xs text-[#71717A]">{t.columns.join(" · ")}</div>
                  <div className="text-[10px] text-[#D4AF37] mt-2 uppercase tracking-widest">
                    {t.beat_count} beat{t.beat_count === 1 ? "" : "s"}
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-4 text-[10px] text-[#71717A] italic">Applying replaces existing beats.</div>
          </div>
        </div>
      )}

      {openBeat && (
        <BeatEditor
          beat={openBeat}
          onClose={() => setOpenBeat(null)}
          onSave={saveBeat}
          onDelete={() => removeBeat(openBeat.id)}
        />
      )}
    </DashboardLayout>
  );
}

function BoardColumn({ title, beats, onAdd, onEdit, onDragStart, onDrop }) {
  const [over, setOver] = React.useState(false);
  return (
    <div
      className={`bg-[#0F0F10] border ${over ? "border-[#D4AF37]" : "border-[#1E1E1E]"} p-3 min-h-[400px]`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={() => { setOver(false); onDrop(); }}
      data-testid={`beat-col-${title.replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{title}</div>
        <div className="text-[10px] text-[#71717A]">{beats.length}</div>
      </div>
      <div className="space-y-2">
        {beats.map((b) => (
          <div key={b.id}
            draggable
            onDragStart={() => onDragStart(b.id)}
            onClick={() => onEdit(b)}
            className="bg-[#141414] border border-[#27272A] hover:border-[#D4AF37] p-3 cursor-grab active:cursor-grabbing group transition-colors"
            data-testid={`beat-card-${b.id}`}>
            <div className="flex items-start gap-2">
              <GripVertical size={12} className="text-[#71717A] mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[#FFFFF0] truncate">{b.title || "Untitled"}</div>
                {b.description && (
                  <div className="text-[11px] text-[#A1A1AA] mt-1 line-clamp-2 leading-relaxed">{b.description}</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <button onClick={onAdd}
        className="mt-2 w-full text-[10px] uppercase tracking-widest text-[#71717A] hover:text-[#D4AF37] py-2 inline-flex items-center justify-center gap-1 border border-dashed border-[#27272A] hover:border-[#D4AF37] transition-colors"
        data-testid={`beat-add-${title.replace(/\s+/g, '-')}`}>
        <Plus size={10} /> Beat
      </button>
    </div>
  );
}

function BeatEditor({ beat, onClose, onSave, onDelete }) {
  const [local, setLocal] = React.useState(beat);
  React.useEffect(() => setLocal(beat), [beat]);
  return (
    <div className="fixed inset-0 bg-[#0A0A0A]/85 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#141414] border border-[#27272A] p-6" onClick={(e) => e.stopPropagation()} data-testid="beat-editor-modal">
        <div className="flex items-baseline justify-between mb-4">
          <div className="uppercase text-xs tracking-widest text-[#D4AF37]">Beat · {local.column}</div>
          <button onClick={onClose} className="text-[#71717A]"><X size={16} /></button>
        </div>
        <label className="block mb-3">
          <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-1">Title</div>
          <input value={local.title} onChange={(e) => setLocal({ ...local, title: e.target.value })}
            className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#D4AF37] outline-none text-[#FFFFF0] px-3 py-2 text-sm"
            data-testid="beat-editor-title" />
        </label>
        <label className="block mb-4">
          <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-1">Description</div>
          <textarea rows={5} value={local.description} onChange={(e) => setLocal({ ...local, description: e.target.value })}
            className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#D4AF37] outline-none text-[#FFFFF0] px-3 py-2 text-sm leading-relaxed"
            data-testid="beat-editor-desc" />
        </label>
        <div className="flex justify-between">
          <button onClick={onDelete} className="border border-[#7F1D1D] text-[#7F1D1D] px-3 py-1.5 text-xs inline-flex items-center gap-2" data-testid="beat-delete">
            <Trash2 size={12} /> Delete
          </button>
          <button onClick={() => onSave(local)} className="bg-[#D4AF37] text-[#0A0A0A] px-5 py-2 text-sm font-medium" data-testid="beat-save">
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
